import { AgentProcessingConfigOptions, ProcessingLenderStrategyType } from '@nemuru-eng/js-api-client';
import { ChooseStrategyType } from '@nemuru-eng/js-api-client/slim';

import { OrchestrationLabelsMap } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/maps/orchestrationLabels.map';
import { OptionListItem } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/OptionListItem';
import { OrchestrationField } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/OrchestrationField';
import { OrchestrationRow } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/OrchestrationRow';
import { RankingAccess } from '@/components/modules/MerchantDetail/MerchantLenderOrchestration/types/RankingAccess';

export class OrchestrationOptionsMap {
  static strategy(options: AgentProcessingConfigOptions): OptionListItem[] {
    return options.processingStrategies.map((value) => ({
      field: OrchestrationField.STRATEGY,
      value,
      titleTextId: OrchestrationLabelsMap.strategy(value),
      summaryTextId: OrchestrationLabelsMap.strategy(value, '_SUMMARY'),
      descriptionTextId: OrchestrationLabelsMap.strategy(value, '_DESCRIPTION'),
    }));
  }

  static mode(options: AgentProcessingConfigOptions): OptionListItem[] {
    return options.processingModes.map((value) => ({
      field: OrchestrationField.MODE,
      value,
      titleTextId: OrchestrationLabelsMap.mode(value, '_TITLE'),
      summaryTextId: OrchestrationLabelsMap.mode(value, '_SUMMARY'),
      descriptionTextId: OrchestrationLabelsMap.mode(value, '_DESCRIPTION'),
    }));
  }

  static stop(options: AgentProcessingConfigOptions): OptionListItem[] {
    return options.stopConditions.map((value) => ({
      field: OrchestrationField.STOP,
      value,
      titleTextId: OrchestrationLabelsMap.stopCondition(value, '_TITLE'),
      summaryTextId: OrchestrationLabelsMap.stopCondition(value, '_SUMMARY'),
      descriptionTextId: OrchestrationLabelsMap.stopCondition(value, '_DESCRIPTION'),
    }));
  }

  /** In waterfall the borrower never sees several offers, so "Elige el cliente" is disabled. */
  static ranking(options: AgentProcessingConfigOptions, strategy: ProcessingLenderStrategyType): OptionListItem[] {
    return options.chooseStrategyTypes.map(({ value, restricted }) => ({
      field: OrchestrationField.RANKING,
      value,
      restricted,
      titleTextId: OrchestrationLabelsMap.chooseStrategy(value),
      summaryTextId: OrchestrationLabelsMap.chooseStrategy(value, '_SUMMARY'),
      descriptionTextId: OrchestrationLabelsMap.chooseStrategy(value, '_DESCRIPTION'),
      disabled: strategy === ProcessingLenderStrategyType.WATERFALL && value === ChooseStrategyType.BORROWER_SELECTION,
      disabledReasonTextId: 'LENDER_ORCHESTRATION_ONLY_PARALLEL',
    }));
  }

  /**
   * Mirrors the api rule: a ranking the caller cannot choose may be re-sent unchanged by every agent
   * of the row, never replaced. If the row's agents disagree on it, nothing can be applied.
   */
  static rankingAccess(options: AgentProcessingConfigOptions, row: OrchestrationRow): RankingAccess {
    const offered = options.chooseStrategyTypes.map(({ value }) => value);
    const stored = [...new Set(row.groups.map((group) => group.config.chooseStrategyType))];

    if (stored.every((value) => offered.includes(value))) {
      return RankingAccess.EDITABLE;
    }

    return stored.length === 1 ? RankingAccess.LOCKED : RankingAccess.READ_ONLY;
  }
}
